import { Badge, DateText } from '@/components';
import type { QueueReason, RunnerStatus } from '@uchat-team-management/shared';
import { type JSX, Show } from 'solid-js';
import { BKK, useNow } from './format';

const RUNNER_TONE = { busy: 'info', idle: 'success', offline: 'neutral' } as const;

export const RunnerStatusBadge = (props: { status: RunnerStatus; stale?: boolean }) => (
  <span class="inline-flex items-center gap-1">
    <Badge tone={RUNNER_TONE[props.status]}>
      {props.status === 'busy' ? 'Busy' : props.status === 'idle' ? 'Idle' : 'Offline'}
    </Badge>
    <Show when={props.stale}>
      <Badge tone="warning">stale</Badge>
    </Show>
  </span>
);

export const MachineStatusBadge = (props: { online: boolean }) => (
  <Badge tone={props.online ? 'success' : 'danger'}>{props.online ? 'Online' : 'Offline'}</Badge>
);

/** Every mock row says so — mock data must never pass for the real thing. */
export const MockBadge = (props: { source: string }) => (
  <Show when={props.source === 'mock'}>
    <Badge tone="warning" class="font-semibold tracking-wide">
      MOCK
    </Badge>
  </Show>
);

const REASON_LABEL: Record<QueueReason, string> = {
  runner: 'Waiting for a runner',
  approval: 'Waiting for approval',
  dependency: 'Waiting for needed jobs',
  concurrency: 'Held by concurrency',
};

export const QueueReasonBadge = (props: { reason: QueueReason | null }) => (
  <Badge tone={props.reason === 'runner' || !props.reason ? 'info' : 'neutral'}>
    {REASON_LABEL[props.reason ?? 'runner']}
  </Badge>
);

const CONCLUSION_TONE: Record<string, 'success' | 'danger' | 'neutral' | 'warning'> = {
  success: 'success',
  failure: 'danger',
  timed_out: 'danger',
  startup_failure: 'danger',
  cancelled: 'neutral',
  skipped: 'neutral',
};

export const JobResultBadge = (props: { status: string; conclusion: string | null }) => (
  <Show when={props.status === 'completed'} fallback={<Badge tone="info">Running</Badge>}>
    <Badge tone={CONCLUSION_TONE[props.conclusion ?? ''] ?? 'warning'}>
      {props.conclusion === 'success'
        ? 'Success'
        : props.conclusion === 'cancelled'
          ? 'Cancelled'
          : props.conclusion === 'failure'
            ? 'Failed'
            : (props.conclusion ?? 'Unknown')}
    </Badge>
  </Show>
);

/** Ticking "x ago" with the full (Bangkok-default) date tooltip on hover. */
export const Ago = (props: { iso: string | null | undefined; class?: string }): JSX.Element => {
  const now = useNow(5000);
  return (
    <DateText
      value={props.iso}
      format="relative"
      now={now()}
      fallbackTimeZone={BKK.fallbackTimeZone}
      empty="never"
      class={props.class}
    />
  );
};

/** Offline banner: what is shown is the last data received, not live. */
export const StaleNotice = (props: { lastSeenAt: string | null }) => (
  <div
    role="status"
    class="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-700/60 dark:bg-amber-900/20 dark:text-amber-200"
  >
    No heartbeat for over 60s — showing stale data from{' '}
    <Show when={props.lastSeenAt} fallback="before the agent ever reported">
      <Ago iso={props.lastSeenAt} class="font-medium" /> (
      <DateText value={props.lastSeenAt} fallbackTimeZone={BKK.fallbackTimeZone} />)
    </Show>
    .
  </div>
);
